import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, UserPlusIcon, FilterIcon, MoreVerticalIcon, CheckCircleIcon, XCircleIcon, ShieldIcon, UserIcon, MailIcon, PhoneIcon, CalendarIcon, MapPinIcon } from 'lucide-react';
interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  status: 'active' | 'inactive';
  lastActive: string;
  location: string;
  phone: string;
  dateJoined: string;
}
const mockUsers: User[] = [{
  id: '1',
  name: 'Sarah Johnson',
  email: 'sarah.johnson@example.com',
  role: 'Municipal Administrator',
  department: 'Administration',
  status: 'active',
  lastActive: '2024-02-15 09:30 AM',
  location: 'Addis Ababa',
  phone: '+251 911 234 567',
  dateJoined: '2023-06-15'
}, {
  id: '2',
  name: 'Michael Tesfaye',
  email: 'michael.t@example.com',
  role: 'Finance Officer',
  department: 'Finance',
  status: 'active',
  lastActive: '2024-02-15 10:15 AM',
  location: 'Bahir Dar',
  phone: '+251 922 345 678',
  dateJoined: '2023-08-01'
}, {
  id: '3',
  name: 'Abeba Kebede',
  email: 'abeba.k@example.com',
  role: 'Service Desk',
  department: 'Customer Service',
  status: 'inactive',
  lastActive: '2024-02-10 02:45 PM',
  location: 'Hawassa',
  phone: '+251 933 456 789',
  dateJoined: '2023-09-20'
}
// Add more mock users as needed
];
export const UserManagementPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const filteredUsers = mockUsers.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) || user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = !selectedRole || user.role === selectedRole;
    const matchesDepartment = !selectedDepartment || user.department === selectedDepartment;
    const matchesStatus = !selectedStatus || user.status === selectedStatus;
    return matchesSearch && matchesRole && matchesDepartment && matchesStatus;
  });
  return <div className="min-h-screen bg-gray-50" data-id="element-3012">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3013">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3014">
          <a href="/" className="hover:text-gray-700" data-id="element-3015">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3016" />
          <a href="/admin" className="hover:text-gray-700" data-id="element-3017">
            Administration
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3018" />
          <span className="text-gray-900" data-id="element-3019">User Management</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3020">
          <div className="flex items-center justify-between" data-id="element-3021">
            <div data-id="element-3022">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-3023">
                User Management
              </h1>
              <p className="mt-1 text-sm text-gray-500" data-id="element-3024">
                Manage system users, roles, and permissions
              </p>
            </div>
            <button onClick={() => setShowAddUserModal(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center" data-id="element-3025">
              <UserPlusIcon className="h-5 w-5 mr-2" data-id="element-3026" />
              Add New User
            </button>
          </div>
        </div>
        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6" data-id="element-3027">
          <div className="p-6" data-id="element-3028">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-3029">
              <div className="flex-1 min-w-0" data-id="element-3030">
                <div className="relative" data-id="element-3031">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-3032" />
                  <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search users by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3033" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4" data-id="element-3034">
                <select className="block w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={selectedRole} onChange={e => setSelectedRole(e.target.value)} data-id="element-3035">
                  <option value="" data-id="element-3036">All Roles</option>
                  <option value="Municipal Administrator" data-id="element-3037">
                    Municipal Administrator
                  </option>
                  <option value="Finance Officer" data-id="element-3038">Finance Officer</option>
                  <option value="Service Desk" data-id="element-3039">Service Desk</option>
                </select>
                <select className="block w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={selectedDepartment} onChange={e => setSelectedDepartment(e.target.value)} data-id="element-3040">
                  <option value="" data-id="element-3041">All Departments</option>
                  <option value="Administration" data-id="element-3042">Administration</option>
                  <option value="Finance" data-id="element-3043">Finance</option>
                  <option value="Customer Service" data-id="element-3044">Customer Service</option>
                </select>
                <select className="block w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} data-id="element-3045">
                  <option value="" data-id="element-3046">All Status</option>
                  <option value="active" data-id="element-3047">Active</option>
                  <option value="inactive" data-id="element-3048">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-3049">
          <div className="min-w-full divide-y divide-gray-200" data-id="element-3050">
            <div className="bg-gray-50 px-6 py-3" data-id="element-3051">
              <div className="grid grid-cols-12 gap-4" data-id="element-3052">
                <div className="col-span-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3053">
                  User
                </div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3054">
                  Role
                </div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3055">
                  Department
                </div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3056">
                  Location
                </div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3057">
                  Status
                </div>
                <div className="col-span-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3058">
                  Actions
                </div>
              </div>
            </div>
            <div className="divide-y divide-gray-200" data-id="element-3059">
              {filteredUsers.map(user => <div key={user.id} className="px-6 py-4 hover:bg-gray-50" data-id="element-3060">
                  <div className="grid grid-cols-12 gap-4" data-id="element-3061">
                    <div className="col-span-3" data-id="element-3062">
                      <div className="flex items-center" data-id="element-3063">
                        <div className="h-10 w-10 flex-shrink-0" data-id="element-3064">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-3065">
                            <UserIcon className="h-6 w-6 text-gray-500" data-id="element-3066" />
                          </div>
                        </div>
                        <div className="ml-4" data-id="element-3067">
                          <div className="text-sm font-medium text-gray-900" data-id="element-3068">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-3069">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2" data-id="element-3070">
                      <div className="text-sm text-gray-900" data-id="element-3071">{user.role}</div>
                    </div>
                    <div className="col-span-2" data-id="element-3072">
                      <div className="text-sm text-gray-900" data-id="element-3073">
                        {user.department}
                      </div>
                    </div>
                    <div className="col-span-2" data-id="element-3074">
                      <div className="text-sm text-gray-900" data-id="element-3075">
                        {user.location}
                      </div>
                    </div>
                    <div className="col-span-2" data-id="element-3076">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-3077">
                        {user.status === 'active' ? <CheckCircleIcon className="mr-1 h-4 w-4" data-id="element-3078" /> : <XCircleIcon className="mr-1 h-4 w-4" data-id="element-3079" />}
                        {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                      </span>
                    </div>
                    <div className="col-span-1 text-right" data-id="element-3080">
                      <button className="text-gray-400 hover:text-gray-500" data-id="element-3081">
                        <MoreVerticalIcon className="h-5 w-5" data-id="element-3082" />
                      </button>
                    </div>
                  </div>
                  <div className="ml-12 mt-2 flex items-center text-xs text-gray-500 space-x-4" data-id="element-3083">
                    <div className="flex items-center" data-id="element-3084">
                      <CalendarIcon className="h-4 w-4 mr-1" data-id="element-3085" />
                      Joined {user.dateJoined}
                    </div>
                    <div className="flex items-center" data-id="element-3086">
                      <PhoneIcon className="h-4 w-4 mr-1" data-id="element-3087" />
                      {user.phone}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Pagination */}
          <div className="bg-white px-6 py-4 border-t border-gray-200" data-id="element-3088">
            <div className="flex items-center justify-between" data-id="element-3089">
              <div className="text-sm text-gray-700" data-id="element-3090">
                Showing <span className="font-medium" data-id="element-3091">1</span> to{' '}
                <span className="font-medium" data-id="element-3092">{filteredUsers.length}</span> of{' '}
                <span className="font-medium" data-id="element-3093">{mockUsers.length}</span> users
              </div>
              <div className="flex space-x-2" data-id="element-3094">
                <button className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-3095">
                  Previous
                </button>
                <button className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-3096">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};